'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { LucideIcon } from 'lucide-react';
import { cn } from '~/lib/utils';
import {
  setSettingsPendingHref,
  useSettingsPendingHref,
} from '~/components/settings/settingsNavigationStore';

export type SettingsNavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
};

export function SettingsNav({
  items,
  onNavigate,
}: {
  items: SettingsNavItem[];
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const pendingHref = useSettingsPendingHref();

  return (
    <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto" aria-label="Settings">
      {items.map((item) => {
        const Icon = item.icon;
        const pathActive = pathname === item.href || pathname?.startsWith(`${item.href}/`);
        const active = pendingHref ? pendingHref === item.href : pathActive;

        return (
          <Link
            key={item.href}
            href={item.href}
            prefetch
            onClick={(e) => {
              onNavigate?.();
              // Modified / non-primary clicks leave this tab on the same route — don't
              // stick the settings shell on a pending skeleton.
              if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
              ) {
                return;
              }
              if (!pathActive) {
                setSettingsPendingHref(item.href);
              }
            }}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-2 text-sm no-underline outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
              active
                ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
            )}
          >
            <Icon className="size-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
